import React, { useState, useContext, useCallback, useEffect } from "react";
import WelcomeScreen from "./FutureValuesMapping/WelcomeScreen";
import Top3Values from "./FutureValuesMapping/Top3Values";
import FutureValuesCompletion from "./FutureValuesMapping/CompletionPages/FutureValuesCompletion";
import { progressService } from "../../../../../services/progressService";
import { UserContext } from "../../../../../UserContext";
import FutureValuesFinalResult from "./FutureValuesMapping/CompletionPages/FutureValuesFinalResult";


const FutureValuesMapping = ({ 
  subsectionId = "f135631c-ac59-4ac1-8d68-7f522948d56f", 
  initialData, 
  onComplete, 
  onToggleImmersive,
  onSubsectionChange,      
}) => {
  const [phase, setPhase] = useState("welcome");
  const [top3Values, setTop3Values] = useState([]);
  const [currentValuesMapping, setCurrentValuesMapping] = useState([]);
  const [futureData, setFutureData] = useState({});
  const [currentIdentityData, setCurrentIdentityData] = useState({});
  const [, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user } = useContext(UserContext);
  const userId = user?.user_id;
  const userName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.firstName || user?.lastName || "User";
  const profileImage = user?.profilePicture || null;

  const isValidUUID = (uuid) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (!userId) return;
      
      try {
        const FUTURE_IDENTITY_SUBSECTION_ID = "a6f1a312-7580-4988-8cc2-64d342be7ab3";
        const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
        const VALUES_MAPPING_SUBSECTION_ID = "4b668e43-680e-4a50-941c-a4eb6a715a88";
        
        const [futureRes, currentRes, valuesRes] = await Promise.all([
          progressService.getUserProgress(userId, FUTURE_IDENTITY_SUBSECTION_ID),
          progressService.getUserProgress(userId, CURRENT_IDENTITY_SUBSECTION_ID),
          progressService.getUserProgress(userId, VALUES_MAPPING_SUBSECTION_ID),
        ]);
        
        // ✅ Handle nested payload structure for all responses
        const currentPayload = currentRes?.data?.payload || currentRes?.data || {};
        const futurePayload = futureRes?.data?.payload || futureRes?.data || {};
        const valuesPayload = valuesRes?.data?.payload || valuesRes?.data || {};

        // ✅ Extract profile image from payload or user context
        const profileImageUrl = currentPayload.profileImage || user?.profilePicture || null;
        
        const futureDataWithProfileImage = {
          ...futurePayload,
          profileImage: profileImageUrl,
        };
        setFutureData(futureDataWithProfileImage);
        
        setCurrentIdentityData(currentPayload);
        setCurrentValuesMapping(valuesPayload.top3Values || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
  
    fetchAllData();
  }, [userId, user?.profilePicture]);

  const loadProgress = useCallback(async () => {
    if (!userId || !subsectionId || !isValidUUID(subsectionId)) {
      return;
    }
    
    try {
      const record = await progressService.getUserProgress(userId, subsectionId);
      
      if (record?.data) {
        // ✅ FIXED: Handle nested payload structure
        const payload = record.data.payload || record.data;
        setTop3Values(payload.top3Values || []);
      }
    } catch (err) {
      console.error("Failed to load progress:", err);
    }
  }, [userId, subsectionId]);

  useEffect(() => {
    if (initialData) {
      // ✅ FIXED: Handle nested payload structure
      const payload = initialData.payload || initialData;
      setTop3Values(payload.top3Values || []);
    }
    
    if (userId && subsectionId && isValidUUID(subsectionId)) {
      loadProgress();
    }
  }, [userId, subsectionId, loadProgress, initialData]);

  const handleStart = () => {
    setPhase("main");
    if (typeof onToggleImmersive === "function") {
      onToggleImmersive(true);
    }
  };

  const handleFinishTop3 = async (values) => {
    setTop3Values(values);
    setPhase("completion");
    setIsLoading(true);
    setError(null);

    try {
      // ✅ FIXED: Add type: "widget" to match pattern
      const dataToSave = {
        type: "widget",
        payload: {
          top3Values: values,
        }
      };

      await progressService.saveProgress(
        userId,
        subsectionId,
        dataToSave,
        "completed"
      );
      
    } catch (err) {
      setError(err.message || "Failed to save future values");
      console.error("Error saving future values:", err);
      setPhase("main");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompletionNext = () => {
    setPhase("finalResult");
    if (typeof onToggleImmersive === "function") {
      onToggleImmersive(false);
    }
  };

  const handleFinalResultComplete = () => {
    if (typeof onComplete === "function") {
      onComplete();
    }
  };

  useEffect(() => {
    if (typeof onToggleImmersive === "function") {
      if (phase === "welcome") onToggleImmersive(false);
      if (phase === "main") onToggleImmersive(true);
      if (phase === "completion") onToggleImmersive(false);
      if (phase === "finalResult") onToggleImmersive(false);
    }
  }, [phase, onToggleImmersive]);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      {phase === "welcome" && <WelcomeScreen onStart={handleStart} />}
      
      {phase === "main" && (
        <Top3Values 
          onFinish={handleFinishTop3}
          futureData={futureData}
          userName={userName}
          profileImage={profileImage}
          initialValues={top3Values.length > 0 ? top3Values : ["", "", ""]}
        />
      )}

      {phase === "completion" && (
        <FutureValuesCompletion
          top3Values={top3Values}
          futureData={futureData}
          userName={userName}
          profileImage={profileImage}
          onNext={handleCompletionNext}
        />
      )}

      {phase === "finalResult" && (
        <FutureValuesFinalResult
          currentIdentityData={currentIdentityData}
          futureData={futureData}
          top3Values={top3Values}
          currentValuesMapping={currentValuesMapping}
          userName={userName}
          profileImage={profileImage}
          userId={userId}
          subsectionId={subsectionId}
          onSubsectionChange={onSubsectionChange}
          onComplete={handleFinalResultComplete}
        />
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {error}
        </div>
      )}
    </div>
  );
};

export default FutureValuesMapping;